import {
  type QueryClient,
  type QueryKey,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useTranslation } from '../contexts/TranslationContext';
import { trackEvent } from '../lib/analytics';
import { funnelsAPI, type LeadsQueryParams, leadsAPI } from '../lib/api';
import { useMixpanel } from '../lib/mixpanel';
import { type RefreshPostsResponse, workflowsAPI } from '../lib/workflowsApi';
import type {
  ActivitiesResponse,
  BoardData,
  Contact,
  CreateLeadPayload,
  FunnelTemplate,
  Lead,
  LeadDetail,
  LeadsResponse,
  Note,
  UpdateLeadPayload,
} from '../types/leads';

/**
 * Refresh everything the lead detail screen reads after a mutation.
 *
 * The timeline used to ride inside the `['lead', id]` payload, so invalidating
 * that key refreshed it for free. It is now its own query
 * (`LeadActivitySection` → `useLeadActivities`), and almost every mutation here
 * writes an `Activity` row — a note, a stage move, an edit, a contact change.
 * Invalidating only the lead query leaves the timeline showing pre-mutation
 * history until its 5-minute staleTime expires.
 *
 * The activities key is a PREFIX on purpose: `useLeadActivities` keys
 * `includeAutomation` into the query, so a fully-specified key would refresh
 * only whichever side of the toggle happens to be on screen.
 */
export function invalidateLeadDetail(queryClient: QueryClient, leadId: string) {
  queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
  queryClient.invalidateQueries({ queryKey: ['lead-activities', leadId] });
}

/**
 * The same refresh, for callers that are not mutations.
 *
 * The lead detail screen invalidates from effects — an agent pipeline finishing,
 * and two enrichment-completion paths — rather than from a mutation hook. Those
 * sites reached for `queryClient` directly and so refreshed only the lead
 * payload, leaving the timeline stale for up to five minutes after the very
 * events that wrote its newest rows. Exposing the helper keeps one definition
 * instead of a fourth hand-rolled copy.
 */
export function useInvalidateLeadDetail() {
  const queryClient = useQueryClient();

  // Stable across renders: these are called from effect bodies, and an unstable
  // identity in a dependency array would re-fire them.
  return useCallback((leadId: string) => invalidateLeadDetail(queryClient, leadId), [queryClient]);
}

export function useLeads(params: LeadsQueryParams, enabled = true) {
  return useQuery<LeadsResponse>({
    queryKey: ['leads', params],
    queryFn: async () => {
      const { data } = await leadsAPI.getAll(params);
      return data;
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}

export const leadDetailQueryKey = (
  leadId: string | undefined,
  organizationId: string | undefined,
) => ['lead', leadId, organizationId] as const;

const LEAD_DETAIL_ORG_INDEX = leadDetailQueryKey(undefined, undefined).length - 1;

export const isOrgLeadDetailQuery = (queryKey: readonly unknown[], organizationId: string) =>
  queryKey[0] === leadDetailQueryKey(undefined, undefined)[0] &&
  queryKey[LEAD_DETAIL_ORG_INDEX] === organizationId;

export function useLead(id: string | undefined, organizationId: string | undefined) {
  return useQuery<LeadDetail>({
    queryKey: leadDetailQueryKey(id, organizationId),
    queryFn: async () => {
      const { data } = await leadsAPI.getOne(id!, organizationId!);
      return data;
    },
    enabled: !!id && !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useCreateLead() {
  const queryClient = useQueryClient();

  return useMutation<Lead, Error, CreateLeadPayload>({
    mutationFn: async (payload) => {
      const { data } = await leadsAPI.create(payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['seller-profiles'] });
        queryClient.invalidateQueries({ queryKey: ['leads'] });
        queryClient.invalidateQueries({ queryKey: ['board'] });
      }, 1500);
    },
  });
}

export function useUpdateLead() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();
  const { currentOrganization } = useAuth();

  return useMutation<Lead, Error, { id: string; payload: UpdateLeadPayload }>({
    mutationFn: async ({ id, payload }) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      const { data } = await leadsAPI.update(id, orgId, payload);
      return data;
    },
    onSuccess: (data, variables) => {
      trackEvent(mp, 'lead.updated', {
        fields_changed: Object.keys(variables.payload),
      });
      queryClient.setQueriesData<LeadDetail>({ queryKey: ['lead', variables.id] }, (old) =>
        old ? { ...old, ...data } : old,
      );
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      invalidateLeadDetail(queryClient, variables.id);
      queryClient.invalidateQueries({ queryKey: ['board'] });
      queryClient.invalidateQueries({ queryKey: ['seller-profiles'] });
    },
  });
}

export function useDeleteLead() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected');
      }
      await leadsAPI.delete(id, currentOrganization.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
    },
  });
}

interface MoveStageContext {
  previousBoards: Array<{ key: QueryKey; data: BoardData | undefined }>;
  fromStage?: { name?: string; position?: number };
}

export interface QualificationBlockedInfo {
  qualificationStatus?: string;
  variables: { leadId: string; stageId: string };
}

export interface UseMoveStageOptions {
  /**
   * Called when the backend rejects the move because the lead is not qualified
   * (HTTP 409, code LEAD_NOT_QUALIFIED). Return true if the caller handled it
   * (e.g. opened the force-qualify modal) to suppress the default toast.
   */
  onQualificationBlocked?: (info: QualificationBlockedInfo) => boolean;
}

export function useMoveStage(options?: UseMoveStageOptions) {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();
  const { showError } = useToast();
  const { t } = useTranslation();

  return useMutation<Lead, Error, { leadId: string; stageId: string }, MoveStageContext>({
    mutationFn: async ({ leadId, stageId }) => {
      const { data } = await leadsAPI.moveStage(leadId, stageId, currentOrganization?.id ?? '');
      return data;
    },
    onMutate: async ({ leadId, stageId }) => {
      await queryClient.cancelQueries({ queryKey: ['board'] });

      const boardQueries = queryClient.getQueriesData<BoardData>({ queryKey: ['board'] });
      const previousBoards = boardQueries.map(([key, data]) => ({ key, data }));

      let movedLead: Lead | undefined;
      for (const [queryKey, boardData] of boardQueries) {
        if (!boardData) continue;
        const updatedStages = boardData.stages.map((stage) => {
          const leadInStage = stage.leads.find((l) => l.id === leadId);
          if (leadInStage) {
            movedLead = leadInStage;
            return {
              ...stage,
              leads: stage.leads.filter((l) => l.id !== leadId),
              count: stage.count - 1,
            };
          }
          return stage;
        });

        if (movedLead) {
          const targetStage = updatedStages.find((s) => s.id === stageId);
          const finalStages = updatedStages.map((stage) => {
            if (stage.id === stageId && targetStage) {
              return {
                ...stage,
                leads: [
                  ...stage.leads,
                  {
                    ...movedLead!,
                    currentStageId: stageId,
                    currentStage: {
                      id: targetStage.id,
                      name: targetStage.name,
                      position: targetStage.position,
                      color: targetStage.color,
                      createdAt: targetStage.createdAt,
                    },
                  },
                ],
                count: stage.count + 1,
              };
            }
            return stage;
          });

          queryClient.setQueryData<BoardData>(queryKey, {
            ...boardData,
            stages: finalStages,
          });
        }
      }

      const fromStage = movedLead?.currentStage
        ? { name: movedLead.currentStage.name, position: movedLead.currentStage.position }
        : undefined;

      return { previousBoards, fromStage };
    },
    onError: (err, variables, context) => {
      if (context?.previousBoards) {
        for (const { key, data } of context.previousBoards) {
          queryClient.setQueryData(key, data);
        }
      }
      const response = (
        err as {
          response?: {
            status?: number;
            data?: { code?: string; qualificationStatus?: string };
          };
        }
      )?.response;

      if (response?.status !== 409) return;

      if (response.data?.code === 'LEAD_NOT_QUALIFIED') {
        const handled =
          options?.onQualificationBlocked?.({
            qualificationStatus: response.data.qualificationStatus,
            variables,
          }) ?? false;
        if (handled) return;
        const key =
          response.data.qualificationStatus === 'pending_validation'
            ? 'leads.qualificationBlocked.pending'
            : 'leads.qualificationBlocked.notQualified';
        showError(t(key));
        return;
      }

      showError(t('leads.moveBlocked'));
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      invalidateLeadDetail(queryClient, variables.leadId);
      queryClient.invalidateQueries({ queryKey: ['board'] });
    },
  });
}

export function useAddNote() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation<Note, Error, { leadId: string; content: string }>({
    mutationFn: async ({ leadId, content }) => {
      const { data } = await leadsAPI.addNote(leadId, content, currentOrganization?.id ?? '');
      return data;
    },
    onSuccess: (_data, variables) => {
      invalidateLeadDetail(queryClient, variables.leadId);
    },
  });
}

export function useLeadActivities(
  leadId: string | undefined,
  limit = 25,
  includeAutomation = false,
) {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id;

  const query = useInfiniteQuery<ActivitiesResponse>({
    queryKey: ['lead-activities', leadId, includeAutomation],
    queryFn: async ({ pageParam }) => {
      const cursor = pageParam as string | undefined;
      const { data } = await leadsAPI.getActivities(
        leadId!,
        orgId!,
        limit,
        cursor,
        includeAutomation,
      );
      return data;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined,
    enabled: !!leadId && !!orgId,
    staleTime: 5 * 60_000,
  });

  const activities = query.data?.pages.flatMap((page) => page.items) ?? [];

  return {
    ...query,
    activities,
  };
}

export function useBoard(
  templateId: string | undefined,
  organizationId: string | undefined,
  viewMode?: 'my' | 'team',
  realtimeConnected?: boolean,
) {
  return useQuery<BoardData>({
    queryKey: ['board', templateId, organizationId, viewMode],
    queryFn: async () => {
      const { data } = await funnelsAPI.getBoard(templateId!, organizationId!, viewMode);
      return data;
    },
    enabled: !!templateId && !!organizationId,
    refetchInterval: realtimeConnected ? false : 30_000,
    staleTime: 5 * 60_000,
  });
}

export function useFunnelTemplates(organizationId: string | undefined) {
  return useQuery<FunnelTemplate[]>({
    queryKey: ['funnelTemplates', organizationId],
    queryFn: async () => {
      const { data } = await funnelsAPI.getTemplates(organizationId!);
      return data;
    },
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export interface LeadStats {
  totalLeads: number;
  stagesBreakdown: Array<{
    id: string;
    name: string;
    color: string;
    position: number;
    count: number;
  }>;
  sourceBreakdown: Array<{
    source: string;
    count: number;
  }>;
  recentActivities: Array<{
    id: string;
    activityType: string;
    description: string | null;
    createdAt: string;
    user: { id: string; fullName: string } | null;
    lead: { id: string; fullName: string | null; email: string | null } | null;
  }>;
}

export function useLeadStats(organizationId: string | undefined) {
  return useQuery<LeadStats>({
    queryKey: ['leadStats', organizationId],
    queryFn: async () => {
      const { data } = await leadsAPI.getStats(organizationId!);
      return data;
    },
    enabled: !!organizationId,
    refetchInterval: 60000,
    staleTime: 60000,
  });
}

export function useAddContact() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation<
    Contact,
    Error,
    {
      leadId: string;
      data: {
        name?: string;
        email?: string;
        phone?: string;
        jobTitle?: string;
        role?: string | null;
      };
    }
  >({
    mutationFn: async ({ leadId, data }) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      const { data: contact } = await leadsAPI.addContact(leadId, orgId, data);
      return contact;
    },
    onSuccess: (_data, variables) => {
      invalidateLeadDetail(queryClient, variables.leadId);
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
    },
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation<
    Contact,
    Error,
    {
      leadId: string;
      contactId: string;
      data: {
        name?: string | null;
        email?: string | null;
        phone?: string | null;
        jobTitle?: string | null;
        role?: string | null;
      };
    }
  >({
    mutationFn: async ({ leadId, contactId, data }) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      const { data: contact } = await leadsAPI.updateContact(leadId, contactId, orgId, data);
      return contact;
    },
    onSuccess: (_data, variables) => {
      invalidateLeadDetail(queryClient, variables.leadId);
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
    },
  });
}

export function useRemoveContact() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation<void, Error, { leadId: string; contactId: string }>({
    mutationFn: async ({ leadId, contactId }) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      await leadsAPI.removeContact(leadId, contactId, orgId);
    },
    onSuccess: (_data, variables) => {
      invalidateLeadDetail(queryClient, variables.leadId);
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
    },
  });
}

export function useSetPrimaryContact() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation<Contact, Error, { leadId: string; contactId: string }>({
    mutationFn: async ({ leadId, contactId }) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      const { data: contact } = await leadsAPI.setPrimaryContact(leadId, contactId, orgId);
      return contact;
    },
    onSuccess: (_data, variables) => {
      invalidateLeadDetail(queryClient, variables.leadId);
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useRescanContact() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation<unknown, Error, { leadId: string; sourceUrl: string }>({
    mutationFn: async ({ leadId, sourceUrl }) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      const socialMedia = sourceUrl.includes('instagram.com') ? 'Instagram' : 'LinkedIn';
      const { data } = await workflowsAPI.triggerEnrichment({
        organizationId: orgId,
        profileUrl: sourceUrl,
        socialMedia: socialMedia as 'LinkedIn' | 'Instagram',
        leadId,
      });
      return data;
    },
    onSuccess: (_data, variables) => {
      invalidateLeadDetail(queryClient, variables.leadId);
    },
  });
}

export function useRefreshPosts() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();
  const { currentOrganization } = useAuth();

  return useMutation<RefreshPostsResponse, Error, { leadId: string }>({
    mutationFn: async ({ leadId }) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('Organization not loaded');
      const { data } = await workflowsAPI.refreshPosts({
        leadId,
        organizationId: orgId,
      });
      return data;
    },
    onSuccess: (_data, variables) => {
      trackEvent(mp, 'lead.posts_refreshed');
      invalidateLeadDetail(queryClient, variables.leadId);
    },
  });
}
